import { supabase } from '../lib/supabase'
import type {
  AdmOrderHeader,
  AdmOrderItem,
  AdmContract,
  AdmContractService,
  AdmNfeHeader,
  AdmNfeItem,
  AdmNfseHeader,
  AdmNfseItem,
  AdmWithoutNfInsert,
  AdmWithoutNfRow,
  UnifiedDocument,
} from '../types/admDespesas'

const WITHOUT_NF_CHUNK = 500

function formatOrderSourceLabel(documentKind: string | null): string {
  const kind = (documentKind ?? '').trim().toLowerCase()
  if (kind === 'orcamento') return 'Orçamento'
  if (kind === 'pedido') return 'Pedido'
  if (documentKind?.trim()) return documentKind.trim()
  return 'Orçamento/Pedido'
}

export const admDespesasService = {
  async listNfe(): Promise<AdmNfeHeader[]> {
    const { data, error } = await supabase
      .from('adm_nfe_header')
      .select('*')
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmNfeHeader[]
  },

  async listNfse(): Promise<AdmNfseHeader[]> {
    const { data, error } = await supabase
      .from('adm_nfse_header')
      .select('*')
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmNfseHeader[]
  },

  async listNfeItems(nfeId: string): Promise<AdmNfeItem[]> {
    const { data, error } = await supabase
      .from('adm_nfe_items')
      .select('*')
      .eq('nfe_id', nfeId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmNfeItem[]
  },

  async listAllNfeItems(): Promise<AdmNfeItem[]> {
    const { data, error } = await supabase
      .from('adm_nfe_items')
      .select('*')
      .is('deleted_at', null)
      .order('nfe_id')

    if (error) throw error
    return (data ?? []) as AdmNfeItem[]
  },

  async listNfseItems(nfseId: string): Promise<AdmNfseItem[]> {
    const { data, error } = await supabase
      .from('adm_nfse_items')
      .select('*')
      .eq('nfse_id', nfseId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmNfseItem[]
  },

  async listAllNfseItems(): Promise<AdmNfseItem[]> {
    const { data, error } = await supabase
      .from('adm_nfse_items')
      .select('*')
      .is('deleted_at', null)
      .order('nfse_id')

    if (error) throw error
    return (data ?? []) as AdmNfseItem[]
  },

  async listOrderHeaders(): Promise<AdmOrderHeader[]> {
    const { data, error } = await supabase
      .from('adm_order_header')
      .select('*')
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmOrderHeader[]
  },

  async listOrderItems(orderId: string): Promise<AdmOrderItem[]> {
    const { data, error } = await supabase
      .from('adm_order_items')
      .select('*')
      .eq('order_id', orderId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmOrderItem[]
  },

  async listAllOrderItems(): Promise<AdmOrderItem[]> {
    const { data, error } = await supabase
      .from('adm_order_items')
      .select('*')
      .is('deleted_at', null)
      .order('order_id')

    if (error) throw error
    return (data ?? []) as AdmOrderItem[]
  },

  async updateOrderHeader(
    id: string,
    patch: {
      document_kind?: string | null
      number?: string | null
      issue_date?: string | null
      issuer_cnpj?: string | null
      issuer_name?: string | null
      recipient_document?: string | null
      recipient_name?: string | null
      total_products?: number | null
      total_freight?: number | null
      total_discount?: number | null
      total_value?: number | null
      payment_method?: string | null
      payment_date?: string | null
      manual_notes?: string | null
      file_name?: string | null
      file_name_storage?: string | null
      payload_json?: Record<string, unknown> | null
    }
  ): Promise<void> {
    const { error } = await supabase.from('adm_order_header').update(patch).eq('id', id).is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteOrderHeader(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_order_header')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async updateOrderItem(
    itemId: string,
    patch: {
      description?: string | null
      ncm?: string | null
      quantity?: number | null
      unit?: string | null
      unit_value?: number | null
      total_value?: number | null
      category_id?: string | null
      cost_center_id?: string | null
    }
  ): Promise<void> {
    const { error } = await supabase.from('adm_order_items').update(patch).eq('id', itemId)

    if (error) throw error
  },

  async softDeleteOrderItem(itemId: string, userId: string, reason?: string | null): Promise<void> {
    const { error } = await supabase
      .from('adm_order_items')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason?.trim() || null,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async listContracts(): Promise<AdmContract[]> {
    const { data, error } = await supabase
      .from('adm_contracts')
      .select('*')
      .is('deleted_at', null)
      .order('start_date', { ascending: false, nullsFirst: false })

    if (error) throw error
    return (data ?? []) as AdmContract[]
  },

  async listContractServices(contractId: string): Promise<AdmContractService[]> {
    const { data, error } = await supabase
      .from('adm_contract_services')
      .select('*')
      .eq('contract_id', contractId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmContractService[]
  },

  async listAllContractServices(): Promise<AdmContractService[]> {
    const { data, error } = await supabase
      .from('adm_contract_services')
      .select('*')
      .is('deleted_at', null)
      .order('contract_id')

    if (error) throw error
    return (data ?? []) as AdmContractService[]
  },

  async insertContract(row: {
    contract_number?: string | null
    counterparty_name?: string | null
    counterparty_document?: string | null
    start_date?: string | null
    end_date?: string | null
    total_value?: number | null
    notes?: string | null
    objective?: string | null
    signature_date?: string | null
    payment_notes?: string | null
    payment_date?: string | null
    manual_notes?: string | null
    contractor_name?: string | null
    contractor_document?: string | null
    import_method?: string | null
    file_name?: string | null
    file_name_storage?: string | null
    payload_json?: Record<string, unknown> | null
  }): Promise<AdmContract> {
    const { data, error } = await supabase
      .from('adm_contracts')
      .insert(row)
      .select('*')
      .single()

    if (error) throw error
    return data as AdmContract
  },

  async updateContract(
    id: string,
    patch: {
      contract_number?: string | null
      counterparty_name?: string | null
      counterparty_document?: string | null
      start_date?: string | null
      end_date?: string | null
      total_value?: number | null
      notes?: string | null
      objective?: string | null
      signature_date?: string | null
      payment_notes?: string | null
      payment_date?: string | null
      manual_notes?: string | null
      contractor_name?: string | null
      contractor_document?: string | null
      import_method?: string | null
      file_name?: string | null
      file_name_storage?: string | null
      payload_json?: Record<string, unknown> | null
    }
  ): Promise<void> {
    const { error } = await supabase
      .from('adm_contracts')
      .update(patch)
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteContract(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_contracts')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async insertContractService(row: {
    contract_id: string
    description?: string | null
    service_code?: string | null
    quantity?: number | null
    unit_value?: number | null
    total_value?: number | null
    category_id?: string | null
    cost_center_id?: string | null
  }): Promise<AdmContractService> {
    const { data, error } = await supabase
      .from('adm_contract_services')
      .insert(row)
      .select('*')
      .single()

    if (error) throw error
    return data as AdmContractService
  },

  async updateContractService(
    id: string,
    patch: {
      description?: string | null
      service_code?: string | null
      quantity?: number | null
      unit_value?: number | null
      total_value?: number | null
      category_id?: string | null
      cost_center_id?: string | null
    }
  ): Promise<void> {
    const { error } = await supabase
      .from('adm_contract_services')
      .update(patch)
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteContractService(itemId: string, userId: string, reason?: string | null): Promise<void> {
    const { error } = await supabase
      .from('adm_contract_services')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason?.trim() || null,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async listAllDocuments(): Promise<UnifiedDocument[]> {
    const [nfeList, nfseList, withoutNfList, contractsList, orderHeaders] = await Promise.all([
      this.listNfe(),
      this.listNfse(),
      this.listWithoutNf(),
      this.listContracts(),
      this.listOrderHeaders(),
    ])

    const unified: UnifiedDocument[] = []

    for (const nfe of nfeList) {
      unified.push({
        id: nfe.id,
        source: 'nfe',
        sourceLabel: 'NF-e',
        issuerName: nfe.issuer_name,
        date: nfe.issue_date,
        totalValue: nfe.total_value,
        fileName: nfe.file_name,
        createdAt: nfe.created_at,
      })
    }

    for (const nfse of nfseList) {
      unified.push({
        id: nfse.id,
        source: 'nfse',
        sourceLabel: 'NFS-e',
        issuerName: nfse.provider_name,
        date: nfse.issue_date,
        totalValue: nfse.net_value ?? nfse.total_services,
        fileName: nfse.file_name,
        createdAt: nfse.created_at,
      })
    }

    for (const row of withoutNfList) {
      unified.push({
        id: row.id,
        source: 'sem_nf',
        sourceLabel: 'Planilha (sem NF)',
        issuerName: row.description,
        date: row.expense_date,
        totalValue: row.amount_paid ?? row.amount_to_pay,
        fileName: row.source_file_name,
        createdAt: row.created_at,
      })
    }

    for (const contract of contractsList) {
      unified.push({
        id: contract.id,
        source: 'contracts',
        sourceLabel: 'Contrato',
        issuerName: contract.counterparty_name,
        date: contract.start_date,
        totalValue: contract.total_value,
        fileName: contract.file_name,
        createdAt: contract.created_at,
      })
    }

    for (const order of orderHeaders) {
      unified.push({
        id: order.id,
        source: 'budgets',
        sourceLabel: formatOrderSourceLabel(order.document_kind),
        issuerName: order.issuer_name,
        date: order.issue_date,
        totalValue: order.total_value,
        fileName: order.file_name,
        createdAt: order.created_at,
      })
    }

    unified.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return unified
  },

  async listWithoutNf(): Promise<AdmWithoutNfRow[]> {
    const { data, error } = await supabase
      .from('adm_without_nf')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmWithoutNfRow[]
  },

  async insertWithoutNfBatch(rows: AdmWithoutNfInsert[]): Promise<void> {
    if (rows.length === 0) return
    for (let i = 0; i < rows.length; i += WITHOUT_NF_CHUNK) {
      const chunk = rows.slice(i, i + WITHOUT_NF_CHUNK)
      const { error } = await supabase.from('adm_without_nf').insert(chunk)
      if (error) throw error
    }
  },

  async updateWithoutNf(id: string, patch: {
    payment_type?: string | null
    cost_center_id?: string | null
    category_id?: string | null
    expense_date?: string | null
    description?: string | null
    installment?: string | null
    amount_paid?: number | null
    amount_to_pay?: number | null
    notes?: string | null
    payment_date?: string | null
    manual_notes?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_without_nf')
      .update(patch)
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteWithoutNf(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_without_nf')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteNfe(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_header')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async softDeleteNfse(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_header')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async updateNfeItem(itemId: string, patch: {
    description?: string | null
    ncm?: string | null
    quantity?: number | null
    unit?: string | null
    unit_value?: number | null
    total_value?: number | null
    category_id?: string | null
    cost_center_id?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_items')
      .update(patch)
      .eq('id', itemId)

    if (error) throw error
  },

  async updateNfseItem(itemId: string, patch: {
    description?: string | null
    service_code?: string | null
    quantity?: number | null
    unit_value?: number | null
    total_value?: number | null
    category_id?: string | null
    cost_center_id?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_items')
      .update(patch)
      .eq('id', itemId)

    if (error) throw error
  },

  async softDeleteNfeItem(itemId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_items')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async softDeleteNfseItem(itemId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_items')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async updateNfeHeader(headerId: string, patch: {
    number?: string | null
    series?: string | null
    issuer_name?: string | null
    issuer_cnpj?: string | null
    recipient_name?: string | null
    recipient_document?: string | null
    access_key?: string
    issue_date?: string | null
    total_products?: number | null
    total_freight?: number | null
    total_discount?: number | null
    total_value?: number | null
    payment_method?: string | null
    payment_date?: string | null
    manual_notes?: string | null
    file_name?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_header')
      .update(patch)
      .eq('id', headerId)

    if (error) throw error
  },

  async updateNfseHeader(headerId: string, patch: {
    number?: string | null
    rps_number?: string | null
    provider_name?: string | null
    provider_cnpj?: string | null
    provider_im?: string | null
    municipality?: string | null
    customer_name?: string | null
    customer_document?: string | null
    access_key?: string
    issue_date?: string | null
    competence_date?: string | null
    total_services?: number | null
    total_discount?: number | null
    net_value?: number | null
    payment_method?: string | null
    payment_date?: string | null
    manual_notes?: string | null
    file_name?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_header')
      .update(patch)
      .eq('id', headerId)

    if (error) throw error
  },
}
